<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from "vue"

interface TurnstileApi {
    render: (container: HTMLElement, options: Record<string, unknown>) => string
    reset: (widgetId: string) => void
    remove: (widgetId: string) => void
}

const props = defineProps<{
    siteKey: string
}>()

const emit = defineEmits<{
    "update:token": [token: string | null]
}>()

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

const container = ref<HTMLElement | null>(null)
const failed = ref(false)
let widgetId: string | null = null

const api = () => (window as unknown as { turnstile?: TurnstileApi }).turnstile

const loadScript = () =>
    new Promise<void>((resolve, reject) => {
        if (api()) {
            resolve()

            return
        }

        let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`)

        if (!script) {
            script = document.createElement("script")
            script.src = SCRIPT_URL
            script.async = true
            document.head.appendChild(script)
        }

        script.addEventListener("load", () => resolve())
        script.addEventListener("error", () => reject(new Error("The security check could not load")))
    })

const reset = () => {
    failed.value = false

    if (widgetId !== null) {
        api()?.reset(widgetId)
    }

    emit("update:token", null)
}

onMounted(async () => {
    try {
        await loadScript()
    } catch {
        failed.value = true
        emit("update:token", null)

        return
    }

    if (!container.value) {
        return
    }

    widgetId =
        api()?.render(container.value, {
            sitekey: props.siteKey,
            appearance: "interaction-only",
            callback: (token: string) => {
                failed.value = false
                emit("update:token", token)
            },
            "expired-callback": () => emit("update:token", null),
            "error-callback": () => {
                failed.value = true
                emit("update:token", null)
            },
        }) ?? null
})

onBeforeUnmount(() => {
    if (widgetId !== null) {
        api()?.remove(widgetId)
    }
})

defineExpose({ reset, failed })
</script>

<template>
    <div>
        <div ref="container" />

        <p
            v-if="failed"
            class="text-sm text-red-600 dark:text-red-400">
            The security check didn't load. Refresh the page and try again.
        </p>
    </div>
</template>
